import * as React from 'react';
import axios from 'axios';
import { IJobSummary, ListJobsResponseBody, TenantType } from '@lct-jobs/core';
import { apiUrl } from 'utils/apiUrl';
import { useAuthContext } from 'contexts/AuthContext';

export interface CatalogJob {
  jobId: string;
  jobName: string;
}

// Every kind of tenant a job can run on; Untyped is a fallback, not a kind of job
const CATALOG_TENANT_TYPES = (Object.values(TenantType) as string[]).filter(
  (tenantType) => tenantType !== 'Untyped',
);

/**
 * The jobs of a region, as the Jobs pages list them (lct-jobs ListJobsByType for each tenant
 * type), so the Executions job filter offers real jobs rather than whatever the loaded
 * executions happen to contain. ListJobsByType reads every page itself and returns all jobs in
 * one response, with no page token.
 */
export const useJobCatalog = (region: string) => {
  const { idToken } = useAuthContext();
  // Read at request time, so a token refresh does not reload the catalog
  const idTokenRef = React.useRef(idToken);
  idTokenRef.current = idToken;
  const [jobs, setJobs] = React.useState<CatalogJob[]>([]);

  React.useEffect(() => {
    let cancelled = false;
    const headers = { Authorization: `Bearer ${idTokenRef.current}` };

    Promise.allSettled(
      CATALOG_TENANT_TYPES.map((tenantType) =>
        axios.get<ListJobsResponseBody>(
          apiUrl('lct', `regions/${region}/tenantTypes/${tenantType}/jobManager/jobs`),
          { headers },
        ),
      ),
    ).then((responses) => {
      if (cancelled) return;
      const byId = new Map<string, CatalogJob>();
      responses.forEach((response) => {
        if (response.status !== 'fulfilled') return;
        (response.value.data?.jobs ?? []).forEach((job: IJobSummary) => {
          if (job.jobId && !byId.has(job.jobId)) {
            byId.set(job.jobId, { jobId: job.jobId, jobName: job.jobName || job.jobId });
          }
        });
      });
      setJobs(
        Array.from(byId.values()).sort((a, b) =>
          a.jobName.localeCompare(b.jobName, undefined, { sensitivity: 'base' }),
        ),
      );
    });

    return () => {
      cancelled = true;
    };
  }, [region]);

  return jobs;
};
